/**
 * Pure helpers that turn the existing API responses into what the position
 * workspace shows. No fetching here, so everything is unit-testable.
 *
 * Data model today (backend unchanged):
 *  - Candidate rows are one per (person, exam set): assigning a person to a second
 *    exam set creates a second row. Rows carry exam_set_id, invite_status,
 *    invite_token, is_shortlisted, recruiter_name, resume fields.
 *  - Interview sessions carry the outcome: status, overall_score, cefr_band,
 *    completed_at, duration_seconds, linked by candidate_id.
 *  - A person is identified by lower-cased email (T0 §3).
 */

export const lowerEmail = (e) => String(e || '').trim().toLowerCase();

const toTime = (iso) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? 0 : t;
};

/** Most recent session per candidate row id. */
export function latestSessionByCandidate(sessions) {
  const by = {};
  (sessions || []).forEach((s) => {
    if (!s || !s.candidate_id) return;
    const prev = by[s.candidate_id];
    if (!prev || toTime(s.created_at) > toTime(prev.created_at)) by[s.candidate_id] = s;
  });
  return by;
}

const IN_PROGRESS = new Set(['in_progress', 'started', 'active']);

/**
 * Normalised result for one candidate row:
 *  done      — completed with a score (or a CEFR band for language exams)
 *  scoring   — completed, AI score not written yet
 *  inprogress— started, not finished (`interrupted` when the candidate dropped off;
 *              they resume on the same link — app/services/exam_resume.py)
 *  invited   — not started (invite pending or sent)
 */
export function resultOf(row, session) {
  const status = String(session?.status || '').toLowerCase();
  const invite = String(row?.invite_status || '').toLowerCase();
  let state = 'invited';
  let value = null;
  let band = null;
  // "incomplete": interrupted and never resumed before the link expired — reported like a finished exam.
  if (status === 'completed' || status === 'incomplete') {
    // The Interview Report's 0–100 headline score (backend reports/adapters.headline_score);
    // older APIs without it: overall_score (0–10) × 10, the same rule.
    const score = session.report_score ?? (session.overall_score != null ? Number(session.overall_score) * 10 : null);
    band = session.cefr_band || null;
    if (score != null && !Number.isNaN(Number(score))) { state = 'done'; value = Number(score); }
    else if (band) state = 'done';
    else state = 'scoring';
  } else if (IN_PROGRESS.has(status) || invite === 'started' || invite === 'in_progress') {
    state = 'inprogress';
  } else if (invite === 'completed') {
    state = 'scoring';
  }
  return {
    row,
    session: session || null,
    batchId: row?.batch_id || session?.batch_id || null,
    state,
    value,
    band,
    invited: (invite !== '' && invite !== 'pending') || Boolean(row?.invite_sent_at),
    completedAt: session?.completed_at || null,
    durationSeconds: session?.duration_seconds ?? null,
    interrupted: state === 'inprogress' && Boolean(session?.interrupted),
    incomplete: status === 'incomplete',
  };
}

export function average(values) {
  const v = values.filter((x) => x != null && !Number.isNaN(Number(x))).map(Number);
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
}

/** Stage per T0 §3: Shortlisted, Not started (no session anywhere), otherwise Needs review. */
export function stageOf(person) {
  if (person.shortlisted) return 'shortlisted';
  if (!person.hasSession) return 'notstarted';
  return 'review';
}

/**
 * Group candidate rows into people (one per lower-cased email). Each person has
 * `results` keyed by exam set id; rows without an exam set go to `unassigned`.
 * A person never appears twice, whatever number of tests they took.
 */
export function groupPeople(candidates, sessions) {
  const sessionBy = latestSessionByCandidate(sessions);
  const people = new Map();
  (candidates || []).forEach((row) => {
    const key = lowerEmail(row.email) || `id:${row.id}`;
    let p = people.get(key);
    if (!p) {
      p = { key, name: row.name || row.email || '—', email: row.email || '', rows: [], results: {}, unassigned: [] };
      people.set(key, p);
    }
    p.rows.push(row);
    const res = resultOf(row, sessionBy[row.id]);
    if (row.exam_set_id) {
      const prev = p.results[row.exam_set_id];
      // Two rows for the same test: keep the more recent one.
      if (!prev || toTime(row.created_at) >= toTime(prev.row.created_at)) p.results[row.exam_set_id] = res;
    } else {
      p.unassigned.push(res);
    }
  });
  return [...people.values()].map((p) => {
    const all = [...Object.values(p.results), ...p.unassigned];
    const named = p.rows.find((r) => r.name) || p.rows[0];
    const person = {
      ...p,
      name: named?.name || p.name,
      shortlisted: p.rows.some((r) => r.is_shortlisted),
      hasSession: all.some((r) => r.session),
      recruiter: (p.rows.find((r) => r.recruiter_name) || {}).recruiter_name || null,
      avg: average(all.filter((r) => r.state === 'done').map((r) => r.value)),
      scoredCount: all.filter((r) => r.state === 'done').length,
      assignedCount: Object.keys(p.results).length,
      all,
    };
    person.stage = stageOf(person);
    return person;
  });
}

/** Score distribution in 5 buckets: 0–20, 20–40, 40–60, 60–80, 80–100. */
export function distribution(values) {
  const dist = [0, 0, 0, 0, 0];
  values.forEach((v) => { dist[Math.min(4, Math.max(0, Math.floor(Number(v) / 20)))] += 1; });
  return dist;
}

/** The hire bar a result is judged against: its batch's (r.hireBar), else `fallback`. */
export const barOf = (r, fallback) => (r?.hireBar != null ? Number(r.hireBar) : Number(fallback));

/**
 * One hire bar for a group of results: the shared batch bar, or the rounded mean when
 * batches differ (`mixed` true). `fallback` when the group has no results.
 */
export function groupHireBar(results, fallback) {
  const bars = [...new Set((results || []).map((r) => barOf(r, fallback)).filter((v) => Number.isFinite(v)))];
  if (!bars.length) return { hireBar: Number(fallback), mixed: false };
  if (bars.length === 1) return { hireBar: bars[0], mixed: false };
  return { hireBar: Math.round(bars.reduce((a, b) => a + b, 0) / bars.length), mixed: true };
}

/**
 * Sets r.hireBar on every result from its batch (`batchesById`, min_hire_score 0–100)
 * and p.hireBar on every person (their results' bar). Mutates the freshly grouped people.
 */
export function applyBatchHireBars(people, batchesById, fallback) {
  (people || []).forEach((p) => {
    (p.all || []).forEach((r) => {
      const b = r.batchId ? batchesById?.[r.batchId] : null;
      const bar = b && b.min_hire_score != null ? Number(b.min_hire_score) : null;
      r.hireBar = Number.isFinite(bar) ? bar : Number(fallback);
    });
    p.hireBar = groupHireBar((p.all || []).filter((r) => r.state === 'done'), fallback).hireBar;
  });
  return people;
}

/** Per-test stats computed from results (T0 §3). `filter` narrows (e.g. by batch).
 *  Each result is compared with its own batch's hire bar; `hireBar` is the fallback. */
export function statsFor(results, hireBar, filter) {
  const rs = filter ? results.filter(filter) : results;
  const done = rs.filter((r) => r.state === 'done' && r.value != null);
  const scored = done.map((r) => r.value);
  const group = groupHireBar(rs, hireBar);
  return {
    total: rs.length,
    done: rs.filter((r) => r.state === 'done' || r.state === 'scoring').length,
    inProgress: rs.filter((r) => r.state === 'inprogress').length,
    notStarted: rs.filter((r) => r.state === 'invited').length,
    scoring: rs.filter((r) => r.state === 'scoring').length,
    avg: average(scored),
    above: done.filter((r) => r.value >= barOf(r, hireBar)).length,
    scoredCount: scored.length,
    dist: distribution(scored),
    hireBar: group.hireBar,
    mixedHireBar: group.mixed,
  };
}

/** All results for one exam set, one per person. */
export function resultsForExamSet(people, examSetId) {
  return people.map((p) => p.results[examSetId]).filter(Boolean);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const asDate = (iso) => {
  const t = iso ? new Date(iso) : null;
  return t && !Number.isNaN(t.getTime()) ? t : null;
};

/** "3 Sep 2026" (fixed month names; browsers disagree on "Sep" vs "Sept"). */
export function fmtDate(iso) {
  const t = asDate(iso);
  return t ? `${t.getDate()} ${MONTHS[t.getMonth()]} ${t.getFullYear()}` : '—';
}

/** "3 Sep". */
export function fmtShortDate(iso) {
  const t = asDate(iso);
  return t ? `${t.getDate()} ${MONTHS[t.getMonth()]}` : '—';
}

export function fmtDuration(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  return `${Math.max(1, Math.round(Number(seconds) / 60))} min`;
}

/** Date column text for a result row: completion date, or when the invite went out. */
export function resultDateText(r) {
  if (r.state === 'done' || r.state === 'scoring') return fmtDate(r.completedAt || r.session?.started_at);
  if (r.state === 'inprogress' && r.session?.total_questions) {
    const s = r.session;
    const resumed = s.resume_count ? ` · resumed ${s.resume_count}/${s.resume_count + (s.resumes_left ?? 0)}` : '';
    return `${s.answered_count ?? 0} of ${s.total_questions} answered${resumed}`;
  }
  if (r.state === 'inprogress') return r.session?.started_at ? `Started ${fmtShortDate(r.session.started_at)}` : 'Started';
  if (r.row?.invite_sent_at) return `Invited ${fmtShortDate(r.row.invite_sent_at)}`;
  return 'Not invited yet';
}

/** Candidate-facing exam URL for a row (same rule as today's Candidate Reports table). */
export function inviteLinkOf(row) {
  const token = row?.invite_token || row?.interview_token;
  if (row?.invite_link || row?.interview_link) return row.invite_link || row.interview_link;
  if (!token || String(row?.id || '').startsWith('synced-')) return '';
  return `${window.location.origin}/interview/${token}`;
}

/** Total questions across JD + proficiency + coding banks (Question bank tab count, T0 #31). */
export function totalQuestionCount(savedQuestions, codingQuestions) {
  return (savedQuestions?.length || 0) + (codingQuestions?.length || 0);
}

/** Sessions with a report: finished, or closed unfinished after an interruption. */
export function hasReport(session) {
  const status = String(session?.status || '').toLowerCase();
  return status === 'completed' || status === 'incomplete';
}
